/** 内部链接可能会出现本身url就是带参数的比如cumulative-deposit?id=24，
 * 然后外面还要再给他一个查询参数就多了? 出现 /cumulative-deposit?id=24?detailId=4&tab=activity 方法用于兼容处理这种情况*/

import { JUMP_ACTION } from "@/const/jump-action";
import { showGullakCashDrawer } from "@/features/gullak-cash/modal";
import { showShareModal } from "@/features/referral/components/Share/modal";
import { showWalletModal } from "@/features/wallet/components/wallet-modal";

/**
 * 内部跳转链接兼容格式化处理
 * @param url 
 * @returns 
 */
export const formatInnerJumpUrl = (url: string) => {
  // 外部链接直接丢出去
  if (/^http/.test(url)) {
    console.error('Not an internal link', url);
    return url;
    // https://web-test.cggame123.com/en/cumulative-deposit?id=24?detailId=4&tab=activity
  }
  // 
  const parts = url.split('?');
  const basePath = parts[0];
  const queryStringParts = parts.slice(1);
  const queryParams = queryStringParts.join('&');
  return queryParams ? `${basePath}?${queryParams}` : basePath;
};

/**
 * 根据链接统一处理站内 / 站外跳转：
 * - 以 http/https 开头：当作站外链接，使用 window.location.href 整页跳转
 * - 以 ?come_action= 或 _other= 开头：执行特定动作（如打开弹窗）
 * - 其它：当作站内路径，优先使用 router.push 保持 SPA 体验；如果没有传 router，则也用 window.location.href
 */
export const jumpByUrl = (link?: string, router?: { push: (url: string) => void; }, moduleName?: string) => {
  if (!link) return;

  // 处理跳转动作
  if (isJumpAction(link)) {
    jumpByAction(link, moduleName);
    return;
  }


  const isExternal = /^https?:\/\//i.test(link);

  // 外链或没有可用的 router 时，直接整页跳转
  if (isExternal || !router) {
    window.location.href = link;
    return;
  }

  // 站内路由，使用 SPA 跳转
  // 判断link是否有trackSource参数，如果有则直接使用，如果没有则取服务端接口参数
  if (moduleName) {
    if (link.includes('trackSource')) {
      router.push(link);
    } else {
      // 如果url有参数，则直接拼接，如果没有则直接拼接
      if (link.includes('?')) {
        router.push(`${link}&trackSource=${moduleName}`);
      } else {
        router.push(`${link}?trackSource=${moduleName}`);
      }
    }
  } else {
    router.push(link);
  }
};

/**
 * 判断链接是否是跳转动作
 * @param {string | null | undefined} link 链接
 * @param {Record<string, string>} [extraParams] 可选，额外参数
 * @returns {boolean} 是否是跳转动作
 */
export const isJumpAction = (link: string | null | undefined): boolean => !!link?.includes('come_action=');



export const jumpByAction = (link: string, source?: string) => {
  if (!link) return;
  const { action } = parseJumpAction(link) || {};
  switch (action) {
    case JUMP_ACTION.OPEN_GULLAK:
      showGullakCashDrawer({ source });
      break;
    case JUMP_ACTION.OPEN_DEPOSIT_CASH:
      showWalletModal({ entrance: 'deposit-cash', customParams: { source_type: source } });
      break;
    case JUMP_ACTION.OPEN_DEPOSIT_CRYPTO:
      showWalletModal({ entrance: 'deposit-crypto', customParams: { source_type: source } });
      break;
    case JUMP_ACTION.OPEN_WITHDRAW_CASH:
      showWalletModal({ entrance: 'withdraw', customParams: { source_type: source, withdrawType: 'cash' } });
      break;
    case JUMP_ACTION.OPEN_WITHDRAW_CRYPTO:
      showWalletModal({ entrance: 'withdraw', customParams: { source_type: source, withdrawType: 'crypto' } });
      break;
    case JUMP_ACTION.OPEN_AFFILIATE_SHARE:
      showShareModal({ source });
      break;
    case JUMP_ACTION.OPEN_WALLET_MODAL:
      showWalletModal({
        entrance: 'root',
        hideWithdraw: false,
        customParams: { source_type: '' }
      });
      break;
    default:
      console.warn('Unknown action:', action);
      break;
  }
};

/**
 * 解析链接中 come_action=xxx 中的值
 * 支持多种格式：
 * - ?come_action=xxx
 * - ?a=11&come_action=xxx
 * - &come_action=xxx
 * - http://example.com/path?other=1&come_action=xxx
 * @param link 包含 come_action 参数的链接
 * @returns { action: string; searchParams: string | null } | null
 */
export const parseJumpAction = (link: string): { action: string; searchParams: string | null; } | null => {
  if (!link) return null;

  // 使用 URLSearchParams 解析
  const params = new URLSearchParams(link);
  const action = params.get('come_action');

  if (!action) {
    return null;
  }

  // 移除 come_action 参数，剩余的作为 searchParams
  params.delete('come_action');
  const remainingParams = params.toString();

  return {
    action,
    searchParams: remainingParams || null,
  };
};
